import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { IconButton } from '@material-ui/core';

import { IconArrowCollapseRight } from 'src/assets/icons/ArrowCollapseRight';
import { VariationFormat } from 'src/components/VariationFormat';
import { slateToText } from 'src/components/RichSlate';
import { TEXT_EDITOR_TYPE, TextDetailsPanel } from 'src/components/TextDetailsPanel';
import { BLOCKED_REASON } from 'src/data/common';
import { GENERATION_TOOL } from 'src/data/generationTool';
import { ASSET_TYPE } from 'src/data/asset';
import { useDetailsData } from './useDetailsData';
import { useHandlers } from './useHandlers';
import { useStyles } from './useStyles';
import { usePrepareTextDetailsPanelProps } from 'src/services';
import { useBenchmarkConfigProps } from '../../services';

const alignBlockedReason = (blockedReason, isScoreLocked) => {
  if (isScoreLocked) {
    return BLOCKED_REASON.limitedScoring;
  }
  return blockedReason;
};

export const VariationDetails = ({ className, variation, portalElement }) => {
  const styles = useStyles();

  const { handleCollapseClick } = useHandlers();

  const text = useMemo(() => slateToText(variation.editorValue), [variation]);
  const locked = variation.isScoreLocked;

  const scoringMethodName = variation?.scoringMethod?.name;
  const secondaryScoringMethodName = variation?.secondaryScoringMethodName;
  const campaignKeywords = variation?.campaignKeywords;
  const {
    scoreData,
    histogramsData,
    blockedReason,
    annotationsData,
    sensitivityData,
    audienceData,
  } = useDetailsData(variation, text);

  const alignedBlockedReason = alignBlockedReason(blockedReason, locked);

  const benchmarkConfigProps = useBenchmarkConfigProps(variation);

  const {
    copyInputProps,
    scoreDetailsProps,
    scoreAdditionalFactorsProps,
    histogramAgeScoreProps,
    histogramGenderScoreProps,
    talkingPointsProps,
    channelPolicyProps,
    audienceChartProps,
    benchmarkProps,
  } = usePrepareTextDetailsPanelProps({
    assetType: variation.assetType,
    generationTool: variation.generationTool,
    scoreData,
    histogramsData,
    annotationsData,
    secondaryScoringMethodName,
    sensitivityData,
    editorContent: variation.editorValue,
    blockedReason: alignedBlockedReason,
    scoringMethodName,
    campaignKeywords,
    textEditorType: TEXT_EDITOR_TYPE.richSlate,
    isScoreLocked: locked,
    forceDisabled: true,
    readOnly: true,
    targetAudienceProps: audienceData,
    benchmarkConfigProps,
  });

  return createPortal(
    <>
      <div
        className={styles.overlay}
        onClick={handleCollapseClick}
      />
      <div
        className={cx(styles.root, className)}
        data-testid="VariationDetailsView"
        data-onboarding="VariationDetailsView"
      >
        <div className={styles.header}>
          <VariationFormat
            className={styles.variationFormat}
            assetType={variation.assetType}
            generationTool={variation.generationTool}
            mixtureSubAssetType={variation.mixtureSubAssetType}
            showInsightsLabel={true}
          />

          <IconButton
            className={styles.buttonCollapse}
            onClick={handleCollapseClick}
            color="default"
            data-testid="VariationDetailsView__CollapseButton"
          >
            <IconArrowCollapseRight />
          </IconButton>
        </div>

        <TextDetailsPanel
          className={styles.textDetails}
          copyInputProps={copyInputProps}
          scoreDetailsProps={scoreDetailsProps}
          scoreAdditionalFactorsProps={scoreAdditionalFactorsProps}
          audienceChartProps={audienceChartProps}
          histogramAgeScoreProps={histogramAgeScoreProps}
          histogramGenderScoreProps={histogramGenderScoreProps}
          talkingPointsProps={talkingPointsProps}
          channelPolicyProps={channelPolicyProps}
          benchmarkProps={benchmarkProps}
        />
      </div>
    </>,
    portalElement
  );
};

VariationDetails.propTypes = {
  className: PropTypes.string,
  variation: PropTypes.shape({
    id: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    assetType: PropTypes.oneOf(Object.values(ASSET_TYPE)).isRequired,
    generationTool: PropTypes.oneOf(Object.values(GENERATION_TOOL)),
  }).isRequired,
  portalElement: PropTypes.instanceOf(Element).isRequired,
};

VariationDetails.defaultProps = {
  className: '',
};
